export type StudioSaveFlags = {
  hasUnsavedDynamicDataChanges?: boolean;
  hasUnsavedModsChanges?: boolean;
  hasUnsavedConnectorKeyRenames?: boolean;
  hasUnsavedJsonDraftChanges?: boolean;
  flowDiagramUnsavedChanges?: boolean;
  hasUnsavedChangesPerVideo?: boolean;
  hasUnsavedChangesLiveControl?: boolean;
};

type FlowDiagramDraft = {
  isDirty?: boolean;
  setNodesAndEdges?: (nodes: unknown[], edges: unknown[]) => void;
};

type FlowDiagramSnapshot = {
  nodes?: unknown[];
  edges?: unknown[];
} | null;

export type SceneDraftSaveResult = {
  savedPerVideoSchema: boolean;
  savedLiveControlSchema: boolean;
};

export type SaveStudioDraftProjectParams = {
  flags: StudioSaveFlags;
  projectWorkspaceFlowDiagram?: FlowDiagramDraft | null;
  flowDiagram?: FlowDiagramSnapshot;
  perVideoCurrentSchema?: string | null;
  liveControlCurrentSchema?: string | null;
  liveControlCurrentData?: unknown;
  updateVideoPartMods: () => Promise<SceneDraftSaveResult | false | void>;
  updateSaveFlowDiagram: () => Promise<unknown>;
  savePerVideoSchema: (
    schema: unknown,
    expectedCurrentSchema?: string
  ) => Promise<unknown>;
  saveLiveControlSchema: (
    schema: unknown,
    data: unknown,
    expectedCurrentSchema?: string,
    expectedCurrentData?: unknown
  ) => Promise<unknown>;
};

function syncFlowDiagramDraft(
  projectWorkspaceFlowDiagram?: FlowDiagramDraft | null,
  flowDiagram?: FlowDiagramSnapshot
): boolean {
  if (!projectWorkspaceFlowDiagram?.isDirty) {
    return false;
  }

  const { nodes, edges } = flowDiagram || {};
  if (!nodes && !edges) {
    return false;
  }

  projectWorkspaceFlowDiagram.setNodesAndEdges?.(nodes || [], edges || []);
  return true;
}

export function hasConnectorDraftChanges(flags: StudioSaveFlags): boolean {
  return Boolean(
    flags.hasUnsavedModsChanges || flags.hasUnsavedConnectorKeyRenames
  );
}

export function shouldSaveSceneDraft(flags: StudioSaveFlags): boolean {
  return Boolean(
    hasConnectorDraftChanges(flags) || flags.hasUnsavedJsonDraftChanges
  );
}

export function hasDataDraftChanges(flags: StudioSaveFlags): boolean {
  return Boolean(
    flags.hasUnsavedDynamicDataChanges ||
      flags.hasUnsavedChangesPerVideo ||
      flags.hasUnsavedChangesLiveControl
  );
}

export function hasUnsavedDraftChanges(flags: StudioSaveFlags): boolean {
  return Boolean(
    shouldSaveSceneDraft(flags) ||
      flags.flowDiagramUnsavedChanges ||
      hasDataDraftChanges(flags)
  );
}

export function hasPublishableChanges(
  flags: StudioSaveFlags,
  selectedProjectIsLatestVersionPublished: boolean
): boolean {
  return (
    !selectedProjectIsLatestVersionPublished || hasUnsavedDraftChanges(flags)
  );
}

export function getUnsavedStudioAreas(flags: StudioSaveFlags): string[] {
  const areas: string[] = [];

  if (hasDataDraftChanges(flags)) {
    areas.push("data");
  }
  if (hasConnectorDraftChanges(flags)) {
    areas.push("connectors");
  }
  if (flags.hasUnsavedJsonDraftChanges) {
    areas.push("animation");
  }
  if (flags.flowDiagramUnsavedChanges) {
    areas.push("flow");
  }

  return areas;
}

export function getUnsavedStudioText(flags: StudioSaveFlags): string {
  const areas = getUnsavedStudioAreas(flags);

  if (!areas.length) {
    return "";
  }

  if (areas.length === 1) {
    return `You have unsaved ${areas[0]} changes`;
  }

  return `You have unsaved ${areas.slice(0, -1).join(", ")} and ${
    areas[areas.length - 1]
  } changes`;
}

export async function saveStudioDraftProject({
  flags,
  projectWorkspaceFlowDiagram,
  flowDiagram,
  perVideoCurrentSchema,
  liveControlCurrentSchema,
  liveControlCurrentData,
  updateVideoPartMods,
  updateSaveFlowDiagram,
  savePerVideoSchema,
  saveLiveControlSchema,
}: SaveStudioDraftProjectParams): Promise<void> {
  const saveSceneDraft = shouldSaveSceneDraft(flags);
  const didSyncFlowDraft = syncFlowDiagramDraft(
    projectWorkspaceFlowDiagram,
    flowDiagram
  );

  let savedSchemas: SceneDraftSaveResult | undefined;
  if (saveSceneDraft) {
    const result = await updateVideoPartMods();
    if (result === false) throw new Error("Save canceled");
    if (result) savedSchemas = result;
  }

  if (didSyncFlowDraft && !saveSceneDraft) {
    if ((await updateSaveFlowDiagram()) === false)
      throw new Error("Save canceled");
  }

  // A scene save can atomically include schemas. Do not replay captured form
  // snapshots afterward: a newer rename may already be pending by this point.
  if (flags.hasUnsavedChangesPerVideo && !savedSchemas?.savedPerVideoSchema) {
    if (perVideoCurrentSchema) {
      if (
        (await savePerVideoSchema(
          JSON.parse(perVideoCurrentSchema),
          perVideoCurrentSchema
        )) === false
      )
        throw new Error("Save canceled");
    } else {
      throw new Error("perVideoCurrentSchema is null");
    }
  }

  if (
    flags.hasUnsavedChangesLiveControl &&
    !savedSchemas?.savedLiveControlSchema
  ) {
    if (liveControlCurrentSchema && liveControlCurrentData) {
      if (
        (await saveLiveControlSchema(
          JSON.parse(liveControlCurrentSchema),
          liveControlCurrentData,
          liveControlCurrentSchema,
          liveControlCurrentData
        )) === false
      )
        throw new Error("Save canceled");
    } else {
      throw new Error(
        "liveControlCurrentSchema or liveControlCurrentData is null"
      );
    }
  }
}
